import { useEffect, useMemo } from "react";
import { useClinicScope } from "@context/ClinicContext";

const everyClinic = () => true;

/**
 * The admin's clinic selector. It renders nothing for a clinic user and
 * nothing when no clinic passes the page's filter; otherwise it shows the
 * eligible clinics with the shared scope's selection. A clinic that has been
 * switched off is never offered; the page passes a stricter `filter` on top
 * when it needs one (reactivation wants a scheduling integration). When the
 * remembered clinic does not pass, the scope moves to the first that does,
 * so what is shown is what is selected.
 */
export default function ClinicPicker({ filter = everyClinic, className = "" }) {
  const { clinicId, setClinicId, clinics, loading, isAdmin } = useClinicScope();

  const eligible = useMemo(
    () => (isAdmin ? clinics.filter((c) => c.is_active !== false && filter(c)) : []),
    [isAdmin, clinics, filter]
  );

  useEffect(() => {
    if (!isAdmin || loading || eligible.length === 0) return;
    if (!eligible.some((c) => c.id === clinicId)) setClinicId(eligible[0].id);
  }, [isAdmin, loading, eligible, clinicId, setClinicId]);

  if (!isAdmin || eligible.length === 0) return null;

  return (
    <select
      aria-label="Clinic"
      value={clinicId ?? ""}
      onChange={(e) => setClinicId(e.target.value)}
      className={`h-9 rounded-lg border border-gray-200 bg-white px-3 pr-8 text-sm text-gray-700 shadow-sm hover:border-gray-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 focus:outline-none ${className}`}
    >
      {eligible.map((c) => (
        <option key={c.id} value={c.id}>
          {c.clinic_name || c.id}
        </option>
      ))}
    </select>
  );
}
